import {
  Box,
  Divider,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import CustomTypography from "../../../Components/Typography";
import {
  supplierInfoBoxStyle,
  nameTextStyle,
  infoTextStyle,
  aboutTextStyle,
  langTextStyle,
  dividerStyle,
  leftSectionStyle,
  supplierStatusBtnStyles,
  VATTextStyle,
  sectionAccordionStyle,
  sectionSummaryStyle,
  sectionDetailsStyle,
  sectionTitleStyle,
} from "../styles/styles";
import CustomTooltip from "../../../Components/CustomTooltip";
import { useSnackbar } from "../../../Components/SnackBar";
import { activateSupplier, pauseSupplier } from "../Helpers";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const SupplierInfo = ({
  supplierInfoData,
  viewType,
  is_paused,
  paused_by,
  supplier_state,
  setSupplierStatusChanged,
}) => {
  const { t } = useTranslation(["supplier-info", "components"]);
  const snackbar = useSnackbar();

  const {
    id,
    name,
    business_name,
    business_number,
    address,
    sections = [],
    email,
    phonePrefix,
    phoneNumber,
    whatsappPrefix,
    whatsappNumber,
    languages,
    display_image_url,
  } = supplierInfoData;

  const phone = [phonePrefix, phoneNumber].filter(Boolean).join("");
  const whatsapp = [whatsappPrefix, whatsappNumber].filter(Boolean).join("");

  const [isPaused, setIsPaused] = useState(is_paused);
  // Who initiated the pause: 'cm', 'supplier', or null. The CM may only lift a
  // pause that the CM started; a supplier-initiated pause is read-only here.
  const [pausedBy, setPausedBy] = useState(paused_by);

  useEffect(() => {
    setIsPaused(is_paused);
    setPausedBy(paused_by);
  }, [is_paused, paused_by]);

  const handleSupplierStatusChange = async (action) => {
    const actions = {
      pause: {
        apiCall: pauseSupplier,
        successMessage: `${name} ${t("action_pause.success")}`,
        errorMessage: `${t("action_pause.failure")}${name}`,
        setStatus: true,
      },
      activate: {
        apiCall: activateSupplier,
        successMessage: `${name} ${t("action_activate.success")}`,
        errorMessage: `${t("action_activate.failure")}${name}`,
        setStatus: false,
      },
    };

    const { apiCall, successMessage, errorMessage, setStatus } =
      actions[action];

    try {
      const response = await apiCall(id);
      if (response.status === 200) {
        snackbar(successMessage, "success");
        setIsPaused(setStatus);
        // A CM action only ever produces a CM-initiated pause (or clears it).
        setPausedBy(setStatus ? "cm" : null);
        setSupplierStatusChanged?.((prev) => {
          const updatedSupplier = {
            supplierId: Number(response.data.supplier_id),
            // Use the concrete state enum (not the generic API "paused"/"active")
            // so the table badge matches the refetched value immediately — a CM
            // pause is always PAUSED_BY_CM.
            status: setStatus ? "PAUSED_BY_CM" : "ACTIVE",
          };
          const filtered = prev.filter(
            (item) => item.supplierId !== updatedSupplier.supplierId
          );
          return [...filtered, updatedSupplier];
        });
      }
    } catch (error) {
      snackbar(errorMessage, "error");
      console.error(`${action} error:`, error);
    }
  };

  const renderSupplierStatusButton = () => {
    if (viewType !== "view") return null;

    // The supplier paused themselves — the CM can't lift it, so show a read-only label.
    // Prefer the explicit paused_by signal, but fall back to the supplier_state enum
    // so this works even if the backend response doesn't include paused_by yet.
    const isPausedBySupplier =
      pausedBy === "supplier" || supplier_state === "PAUSED_BY_SUPPLIER";
    if (isPaused && isPausedBySupplier) {
      return (
        <CustomTypography props={{ ...infoTextStyle, mt: "12px", color: "#fcae62" }}>
          {t("status_paused_by_supplier")}
        </CustomTypography>
      );
    }

    const buttonConfig = isPaused
      ? {
          action: "activate",
          label: t("action_activate.label"),
          iconPath: "/icons/play-btn-icon.svg",
        }
      : {
          action: "pause",
          label: t("action_pause.label"),
          iconPath: "/icons/pause-btn-icon.svg",
        };

    return (
      <Button
        sx={supplierStatusBtnStyles}
        onClick={() => handleSupplierStatusChange(buttonConfig.action)}
      >
        {buttonConfig.iconPath && (
          <img
            src={buttonConfig.iconPath}
            alt={`${buttonConfig.action}_icon`}
          />
        )}
        {buttonConfig.label}
      </Button>
    );
  };

  return (
    <Box sx={leftSectionStyle}>
      <Box sx={supplierInfoBoxStyle}>
        <Box display="flex" alignItems="center" gap="12px">
          <img
            src={display_image_url}
            alt="avatar"
            width={40}
            height={40}
            style={{ borderRadius: "100%" }}
          />
          <CustomTypography props={nameTextStyle}>{name}</CustomTypography>
        </Box>

        <Box mt="10px">
          <CustomTypography props={infoTextStyle}>
            {business_name} | {business_number}
          </CustomTypography>
          <CustomTypography props={infoTextStyle}>{address}</CustomTypography>
          {renderSupplierStatusButton()}
        </Box>

        <Divider sx={dividerStyle} />

        {/* One row per section the supplier filled in on Business Settings
            (Services / Store / Restaurant): its title and the supplier's own
            name for it; the description opens underneath. */}
        {sections.length ? (
          <Box>
            <CustomTypography props={infoTextStyle}>
              {t("boxes.about")}
            </CustomTypography>
            <Box display="flex" flexDirection="column" gap="8px" mt="12px">
              {sections.map((section) => (
                <Accordion
                  key={section.type}
                  disableGutters
                  disabled={!section.description}
                  sx={sectionAccordionStyle}
                >
                  <AccordionSummary
                    expandIcon={section.description ? <ExpandMore /> : null}
                    sx={sectionSummaryStyle}
                  >
                    <Box>
                      <CustomTypography props={sectionTitleStyle}>
                        {t(`sections.${section.type}`)}
                      </CustomTypography>
                      {section.name ? (
                        <CustomTypography props={infoTextStyle}>
                          {section.name}
                        </CustomTypography>
                      ) : null}
                    </Box>
                  </AccordionSummary>
                  {section.description ? (
                    <AccordionDetails sx={sectionDetailsStyle}>
                      <CustomTypography props={{ ...aboutTextStyle, mt: 0 }}>
                        {section.description}
                      </CustomTypography>
                    </AccordionDetails>
                  ) : null}
                </Accordion>
              ))}
            </Box>
          </Box>
        ) : null}

        {languages.length ? (
          <Box mt="32px">
            <CustomTypography props={infoTextStyle}>
              {t("boxes.languages")}
            </CustomTypography>
            <Box display="flex" flexWrap="wrap" mt="12px" gap="4px">
              {languages.map((lang, index) => (
                <CustomTypography key={index} props={langTextStyle}>
                  {t("languages." + lang, { ns: "components" })}
                  {index !== languages.length - 1 && " |"}
                </CustomTypography>
              ))}
            </Box>
          </Box>
        ) : null}

        {email || phone || whatsapp ? (
          <Box mt="32px">
            <CustomTypography props={infoTextStyle}>
              {t("boxes.contact")}
            </CustomTypography>
            <Box display="flex" gap="4px" mt="12px" ml="-8px">
              {email && (
                <CustomTooltip
                  title={t("tooltips.copy_email")}
                  placement="bottom"
                >
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(email);
                      snackbar(t("messages.email_copied_part1") + name + t("messages.email_copied_part2"), "success");
                    }}
                  >
                    <img src="/mail_icon.svg" alt="email" />
                  </IconButton>
                </CustomTooltip>
              )}
              {phone && (
                <CustomTooltip
                  title={t("tooltips.copy_phone")}
                  placement="bottom"
                >
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(phone);
                      snackbar(t("messages.phone_copied_part1") + name + t("messages.phone_copied_part2"), "success");
                    }}
                  >
                    <img src="/phone_icon.svg" alt="phone" />
                  </IconButton>
                </CustomTooltip>
              )}
              {(whatsapp || phone) && (
                <CustomTooltip
                  title={t("tooltips.whatsapp")}
                  placement="bottom"
                >
                  <IconButton
                    onClick={() =>
                      window.open(`https://wa.me/${whatsapp || phone}`)
                    }
                  >
                    <img src="/whatsapp_icon.svg" alt="whatsapp" />
                  </IconButton>
                </CustomTooltip>
              )}
            </Box>
          </Box>
        ) : null}

        <CustomTypography props={{ ...VATTextStyle, mt: "32px" }}>
          {viewType !== "viewAsCommunityManager"
            ? t("prices_include_vat")
            : t("prices_include_vat_community_manager")}
        </CustomTypography>
      </Box>
    </Box>
  );
};

export default SupplierInfo;
